import {ref, computed} from 'vue';
import {useHttpRequest} from "~/composables/useHttpRequest";
import {endpoints} from "~/network/endpoints.js";
import {useDates} from "~/composables/useDates";

export function useInviteLink() {
    const {httpRequest} = useHttpRequest();
    const {
        items: inviteLinks,
        item: inviteLink,
        getItems,
        getItem : getInviteLink,
        itemPending : inviteLinkPending,
        hasItemErrors : hasInviteLinkErrors,
        itemErrors : inviteLinkErrors,
        createItem,
        updateItem,
        deleteItem,
        formPending,
        formErrors,
        hasFormErrors,
        listPending,
    } = useCrudApi('api/v2/inviteLinks');

    const unlockPending = ref(false);
    const unlockError = ref(null);

    const getInviteLinksForEntity = async (referenceType, referenceId) => {
        // The API filter property is EntityReferenceTypes (not ReferenceTypes).
        await getItems(1, 100, {
            entityReferenceTypes: [referenceType],
            referenceIds: [referenceId],
        });
        return inviteLinks.value;
    };

    // Shared form <-> API transforms for join links (used by the admin links page and
    // the entity join-links dialog): the form edits date + separate HH:mm time fields,
    // the API stores single datetimes. Converters are resolved lazily because
    // useInviteLink() is also called outside component setup (useAuthService).
    const transformInviteLinkItem = (item) => {
        const {timeOfDay} = useDates();
        const newItem = {...item};
        // The date half stays the stored ISO value — TapDatePicker renders it in
        // the user's own order. Only the time is split out for its select.
        if (newItem.startsAt) {
            newItem.startsAtTime = timeOfDay(newItem.startsAt);
        }
        if (newItem.expiresAt) {
            newItem.expiresAtTime = timeOfDay(newItem.expiresAt);
        }
        return newItem;
    };

    const inviteLinkBeforeSave = (item) => {
        const {withTimeOfDay} = useDates();
        const newItem = {...item};
        // Merge the chosen time onto the stored date rather than pasting two
        // strings together and re-parsing against a fixed pattern.
        if (newItem.startsAt) {
            newItem.startsAt = withTimeOfDay(newItem.startsAt, newItem.startsAtTime || '00:00');
        }
        if (newItem.expiresAt) {
            newItem.expiresAt = withTimeOfDay(newItem.expiresAt, newItem.expiresAtTime || '00:00');
        }
        delete newItem.startsAtTime;
        delete newItem.expiresAtTime;
        if (newItem.usageLimit) {
            newItem.usageLimit = parseInt(newItem.usageLimit) || null;
        }
        if (newItem.divisionSize) {
            newItem.divisionSize = parseInt(newItem.divisionSize) || null;
        }
        return newItem;
    };

    const unlockInviteLink = async (id, password) => {
        unlockPending.value = true;
        unlockError.value = null;
        try {
            const res = await httpRequest(endpoints.inviteLinks.unlock(id), {password});
            // Update the inviteLink ref with the new data containing authId
            if (res) {
                inviteLink.value = res;
            }
            return res;
        } catch (e) {
            unlockError.value = e;
            throw e;
        } finally {
            unlockPending.value = false;
        }
    };

    // Internal method used by useAuthService - state managed there
    // `extra` carries the entry fee acceptance (acceptEntryFeeTerms,
    // entryFeeTermsVersion) for a paid competition; empty keeps today's body.
    const joinInviteLink = async (id, authId, message, extra = {}) => {
        const data = {...extra};
        if (authId) {
            data.authId = authId;
        }
        if (message) {
            data.message = message;
        }
        const res = await httpRequest(endpoints.inviteLinks.join(id), data);
        return res;
    };

    const getInviteLinkUrl = (inviteLink) => {
        if(inviteLink.shortUrl) {
            return inviteLink.shortUrl;
        }
        const config = useRuntimeConfig();
        return `${config.public.baseUrl}/invite/${inviteLink.slug}`;
    };

    const copyInviteLinkUrl = async (inviteLink) => {
        try {
            await navigator.clipboard.writeText(getInviteLinkUrl(inviteLink));
            return true
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
            return false;
        }
    };

    return {
        inviteLinks,
        inviteLink,
        getItems,
        getInviteLink,
        inviteLinkPending,
        hasInviteLinkErrors,
        inviteLinkErrors,
        createItem,
        updateItem,
        deleteItem,
        getInviteLinksForEntity,
        transformInviteLinkItem,
        inviteLinkBeforeSave,
        unlockInviteLink,
        unlockPending,
        unlockError,
        joinInviteLink, // Internal use only - used by useAuthService
        getInviteLinkUrl,
        copyInviteLinkUrl,
        formPending,
        formErrors,
        hasFormErrors,
        listPending,
    };
}
